import React, { useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { bindFbrosMoreAnchors, type HomeDescriptionParts } from '@lib/fbros-more';

/**
 * Homepage-only "Show more" wrapper.
 * The first paint keeps the article open. The layout effect then collapses it,
 * so a failed script still leaves the full text in the page.
 */
const HomeDescription: React.FC<HomeDescriptionParts> = ({ visible, hidden }) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const scrollOnCollapse = useRef(false);
  const [ready, setReady] = useState(false);
  const [expanded, setExpanded] = useState(true);

  useLayoutEffect(() => {
    setReady(true);
    setExpanded(false);
  }, []);

  useLayoutEffect(() => {
    if (!scrollOnCollapse.current || expanded) return;
    scrollOnCollapse.current = false;
    const root = rootRef.current;
    if (!root) return;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 'auto'
      : 'smooth';
    root.scrollIntoView({ block: 'start', behavior });
  }, [expanded]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || !ready) return;
    return bindFbrosMoreAnchors(root, () => {
      flushSync(() => setExpanded(true));
    });
  }, [ready]);

  const collapsed = ready && !expanded;

  const onToggle = () => {
    if (expanded) {
      scrollOnCollapse.current = true;
      setExpanded(false);
      return;
    }
    setExpanded(true);
  };

  return (
    <div
      ref={rootRef}
      className={`fbros-more${ready ? ' is-ready' : ''}${collapsed ? ' is-collapsed' : ''}`}
      data-expanded={collapsed ? 'false' : 'true'}
    >
      <div className="fbros-more__visible" dangerouslySetInnerHTML={{ __html: visible }} />
      <div
        className="fbros-more__hidden"
        id="fbros-more-content"
        inert={collapsed ? true : undefined}
      >
        <div className="fbros-more__panel" dangerouslySetInnerHTML={{ __html: hidden }} />
      </div>
      <div className="fbros-more__bar">
        <button
          type="button"
          className="fbros-more__btn"
          aria-expanded={ready && expanded}
          aria-controls="fbros-more-content"
          onClick={onToggle}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            onToggle();
          }}
        >
          <span className="fbros-more__label">{ready && expanded ? 'Show less' : 'Show more'}</span>
          <span className="fbros-more__icon" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export default HomeDescription;
